import { Edit, Help } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';

import HGeneric from '../helpers/HGeneric';
import { updateStateAttribute } from '../helpers/helper';
import CButton from './CButton';
import { type TPandasDataFrameValue } from './TPandasDataFrameValue';
import { type TTableMutableRowContext } from './TTableMutableRowContext';
import XDDropdown, { type IXDDropdownBroadcastedComponents } from './selection/XDDropdown';

interface State {
  broadcastedComponents?: IXDDropdownBroadcastedComponents;
}

export default function CCellClickToEdit(props: {
  context: TTableMutableRowContext;
  children?: React.ReactNode;
  style?: Pick<React.CSSProperties, 'justifyContent' | 'textAlign'>;
  extraWidth?: number;
  /**
   * Always renders button, as opposed to the children on new rows.
   */
  alwaysRenderButton?: boolean;
  valueFormatting?: (value: TPandasDataFrameValue) => React.ReactNode;
  renderUnknown?: boolean;
  disableEditing?: boolean;
}) {
  const [state, setState] = useState<State>({});
  const componentRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOnEnter = (ev: KeyboardEvent) => {
      if (ev.key === 'Enter') {
        state.broadcastedComponents?.closeDropdown?.();
      }
    };

    if (state.broadcastedComponents?.closeDropdown) {
      componentRef.current?.querySelector('textarea')?.select();
      componentRef.current?.querySelector('input')?.select();
    }

    window.addEventListener('keydown', closeOnEnter);
    return () => {
      window.removeEventListener('keydown', closeOnEnter);
    };
  }, [state.broadcastedComponents]);

  return (
    <>
      {!props.context.rowName.includes('-') || props.alwaysRenderButton ? (
        props.renderUnknown ? (
          <span className={'px-4 opacity-50'}>
            <Help />
          </span>
        ) : props.disableEditing ? (
          (props.valueFormatting?.(props.context.value) ?? props.context.value)
        ) : (
          <XDDropdown
            button={
              <div ref={buttonRef}>
                <CButton style={{ width: '100%', textAlign: 'left', justifyContent: 'center', ...props.style }}>
                  {!['', null, undefined].includes(props.context.value as any) && (
                    <>
                      <span className={'!normal-case !font-normal !w-full'}>
                        {props.valueFormatting ? props.valueFormatting(props.context.value) : props.context.value}
                      </span>
                      &nbsp;&nbsp;
                    </>
                  )}
                  <Edit className={'opacity-50 -mt-0.5 !text-xs'} />
                </CButton>
              </div>
            }
            usePortal
            closeOnAnyClick={false}
            onUpdatedBroadcastedComponents={(components) => {
              updateStateAttribute<State>({ broadcastedComponents: components }, setState);
            }}
            style={{
              width:
                (buttonRef.current?.getBoundingClientRect()?.width ?? 0) +
                HGeneric.getRemInPx() * (props.extraWidth !== undefined ? props.extraWidth : 3),
              top: (buttonRef.current?.getBoundingClientRect().height ?? 0) * -1 + HGeneric.getRemInPx() * -2,
              left:
                (buttonRef.current?.getBoundingClientRect()?.left ?? 0) -
                HGeneric.getRemInPx() * (props.extraWidth !== undefined ? props.extraWidth / 2 : 1.5)
            }}
            windowAnchor={'left'}
          >
            <div ref={componentRef}>{props.children}</div>
          </XDDropdown>
        )
      ) : (
        props.children
      )}
    </>
  );
}
